import { HttpClient } from '@angular/common/http';
import { inject } from '@angular/core';
import { environment } from '@env/environment';
import { Apollo, gql } from 'apollo-angular';
import { firstValueFrom } from 'rxjs';
import { CompanyColors } from 'src/app/models/company.model';
import { BrandingService } from './branding.service';
import { LanguageService } from './language.service';

type ColorsByCompany = Record<string, CompanyColors>;

const DEFAULT_COLORS: CompanyColors = {
  primaryColor: '#c54318',
  secondaryColor: '#ffffff',
};

// Served from public/img (S3 assets prefix outside local dev). Used whenever no
// studio logo is available (no companyId, logo request failed, or the
// studio has none) so the header is never an empty bar.
const DEFAULT_LOGO_URL = `${environment.ASSET_URL}/img/gym-scan-logo.png`;

const GET_COMPANY_LOGO = gql`
  query GetCompanyLogo($companyId: String!) {
    companyLogo(where: { id: $companyId }) {
      logoUrl
    }
  }
`;

export function initApp() {
  return async () => {
    const langService = inject(LanguageService);
    const apollo = inject(Apollo);
    const http = inject(HttpClient);
    const brandingService = inject(BrandingService);

    langService.init();

    /**
     * QR-Deeplink = /report?equipmentId=abc&companyId=xyz
     */
    const companyId = new URLSearchParams(window.location.search).get(
      'companyId',
    );

    // Colors and logo are independent branding sources fetched from
    // independent backends (an S3 asset vs. the GraphQL API). They're
    // fetched and error-handled separately so a failure in one (e.g. the
    // colors asset being temporarily unreachable) can never also silently
    // skip the other, the way a single try/catch around both previously did.
    const [colors, logoUrl] = companyId
      ? await Promise.all([
          fetchCompanyColors(http, companyId),
          fetchCompanyLogo(apollo, companyId),
        ])
      : [DEFAULT_COLORS, undefined];

    brandingService.applyBranding({
      primaryColor: colors.primaryColor,
      secondaryColor: colors.secondaryColor,
      logoUrl: logoUrl ?? DEFAULT_LOGO_URL,
    });
  };
}

async function fetchCompanyColors(
  http: HttpClient,
  companyId: string,
): Promise<CompanyColors> {
  try {
    // INFRASTRUCTURE, not a frontend concern: this S3 asset must have a CORS
    // policy that allows the actual frontend origins (local dev and every
    // deployed environment) to fetch it directly via the browser. If this
    // request fails with a CORS error, fix the bucket's CORS config — do not
    // add a proxy/no-cors workaround or hardcode colors here.
    const colorsByCompany = await firstValueFrom(
      http.get<ColorsByCompany>(environment.COMPANY_COLORS_URL, {
        responseType: 'json',
      }),
    );
    return findCompanyColors(colorsByCompany, companyId) ?? DEFAULT_COLORS;
  } catch (err) {
    console.error('Failed to load company colors', err);
    return DEFAULT_COLORS;
  }
}

// The company id in the report URL (Auth0 org id, e.g. "org_FrKHfaO4ibvdHgtR")
// and the keys in colorCodes.json aren't guaranteed to share the same casing
// — the asset is maintained independently of the Auth0/GraphQL source of
// truth. This only affects the colors lookup: `companyId` itself is never
// modified, so the GraphQL logo query and any other backend call still use
// the exact original casing.
function findCompanyColors(
  colorsByCompany: ColorsByCompany,
  companyId: string,
): CompanyColors | undefined {
  const normalizedId = companyId.toLowerCase();
  const match = Object.entries(colorsByCompany).find(
    ([key]) => key.toLowerCase() === normalizedId,
  );
  return match?.[1];
}

async function fetchCompanyLogo(
  apollo: Apollo,
  companyId: string,
): Promise<string | undefined> {
  try {
    const result = await firstValueFrom(
      apollo.query<{ companyLogo: { logoUrl?: string } | null }>({
        query: GET_COMPANY_LOGO,
        variables: { companyId },
        fetchPolicy: 'network-only',
      }),
    );
    return result?.data?.companyLogo?.logoUrl ?? undefined;
  } catch (err) {
    console.error('Failed to load company logo', err);
    return undefined;
  }
}
